import { create } from "zustand";
import { ApiError, projectApi, type RemoteUser } from "@/services/projectApi";

type BackendStatus = "checking" | "available" | "unavailable";

interface BackendState {
  status: BackendStatus;
  user: RemoteUser | null;
  csrfToken: string | null;
  panelOpen: boolean;
  busy: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  openPanel: () => void;
  closePanel: () => void;
  clearError: () => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const message = (error: unknown) => error instanceof ApiError || error instanceof Error ? error.message : "Request failed.";

export const useBackendStore = create<BackendState>((set, get) => ({
  status: "checking", user: null, csrfToken: null, panelOpen: false, busy: false, error: null,
  initialize: async () => {
    try { const result = await projectApi.me(); set({ status: "available", user: result.user, csrfToken: result.csrfToken }); }
    catch { set({ status: "unavailable", user: null, csrfToken: null }); }
  },
  openPanel: () => set({ panelOpen: true, error: null }),
  closePanel: () => set({ panelOpen: false, error: null }),
  clearError: () => set({ error: null }),
  login: async (email, password) => {
    set({ busy: true, error: null });
    try { const result = await projectApi.login(email, password); set({ user: result.user, csrfToken: result.csrfToken, status: "available", busy: false }); }
    catch (error) { set({ error: message(error), busy: false }); throw error; }
  },
  register: async (email, password) => {
    set({ busy: true, error: null });
    try { const result = await projectApi.register(email, password); set({ user: result.user, csrfToken: result.csrfToken, status: "available", busy: false }); }
    catch (error) { set({ error: message(error), busy: false }); throw error; }
  },
  logout: async () => {
    const token = get().csrfToken;
    if (!token) return;
    set({ busy: true, error: null });
    try { await projectApi.logout(token); set({ user: null, csrfToken: null, busy: false }); }
    catch (error) { set({ error: message(error), busy: false }); }
  },
}));
